import React, {useMemo, useState} from 'react';
import {useDispatch} from 'react-redux';
import DatePicker from 'react-datepicker';
import {downloadWeatherStationPdf} from '../store/actions/weatherDataReportsActions';
import './WeatherStationPdfReportModal.scss';

const toValidDate = value => {
    if (!value) return null;
    const date = value instanceof Date ? new Date(value) : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};

const startOfDay = date => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const formatLocalDate = date => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const maximumDateTo = dateFrom => {
    const year = dateFrom.getFullYear();
    const month = dateFrom.getMonth() + 12;
    const day = dateFrom.getDate();
    const lastDayOfTargetMonth = new Date(year, month + 1, 0).getDate();
    const result = new Date(year, month, Math.min(day, lastDayOfTargetMonth));
    result.setDate(result.getDate() - 1);
    return result;
};

const WeatherStationPdfReportModal = ({filters, closeMethod, t}) => {
    const dispatch = useDispatch();
    const today = startOfDay(new Date());
    const initialFrom = toValidDate(filters.dateFrom);
    const initialTo = toValidDate(filters.dateTo);
    const [dateFrom, setDateFrom] = useState(initialFrom ? startOfDay(initialFrom) : null);
    const [dateTo, setDateTo] = useState(initialTo ? startOfDay(initialTo) : today);
    const [temperature, setTemperature] = useState(true);
    const [rainPrecipitation, setRainPrecipitation] = useState(true);
    const [includeAnalysis, setIncludeAnalysis] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [downloadError, setDownloadError] = useState('');

    const errors = useMemo(() => {
        const validationErrors = [];
        if (!dateFrom || !dateTo) {
            validationErrors.push(t.gettext('Both dates are required.'));
        } else {
            if (dateFrom > dateTo) {
                validationErrors.push(t.gettext('Date from cannot be later than date to.'));
            }
            if (dateTo > today) {
                validationErrors.push(t.gettext('The report cannot include future dates.'));
            }
            if (dateTo > maximumDateTo(dateFrom)) {
                validationErrors.push(t.gettext('The date range cannot be longer than 12 months.'));
            }
        }
        if (!temperature && !rainPrecipitation) {
            validationErrors.push(t.gettext('Select at least one report parameter.'));
        }
        return validationErrors;
    }, [dateFrom, dateTo, temperature, rainPrecipitation, t, today]);

    const download = () => {
        if (errors.length || submitting) return;

        const parameters = [];
        if (temperature) parameters.push('TEMPERATURE');
        if (rainPrecipitation) parameters.push('RAIN_PRECIPITATION');

        setSubmitting(true);
        setDownloadError('');
        dispatch(downloadWeatherStationPdf({
            weatherStationId: filters.weatherStationId,
            dateFrom: formatLocalDate(dateFrom),
            dateTo: formatLocalDate(dateTo),
            parameters,
            includeAnalysis
        }))
            .then(closeMethod)
            .catch(() => {
                setDownloadError(t.gettext('The PDF report could not be generated. Please try again.'));
                setSubmitting(false);
            });
    };

    return (
        <div className="weatherStationPdfReportModal">
            <div className="pdfDateFields">
                <label>
                    <span>{t.gettext('Date from')}</span>
                    <DatePicker
                        selected={dateFrom}
                        onChange={setDateFrom}
                        maxDate={today}
                        dateFormat="dd/MM/yyyy"
                        placeholderText={t.gettext('Select date')}
                    />
                </label>
                <label>
                    <span>{t.gettext('Date to')}</span>
                    <DatePicker
                        selected={dateTo}
                        onChange={setDateTo}
                        maxDate={today}
                        dateFormat="dd/MM/yyyy"
                        placeholderText={t.gettext('Select date')}
                    />
                </label>
            </div>

            <div className="pdfParameters">
                <div className="pdfSectionTitle">{t.gettext('Report parameters')}</div>
                <label>
                    <input type="checkbox" checked={temperature}
                           onChange={event => setTemperature(event.target.checked)}/>
                    <span>{t.gettext('Temperature')}</span>
                </label>
                <label>
                    <input type="checkbox" checked={rainPrecipitation}
                           onChange={event => setRainPrecipitation(event.target.checked)}/>
                    <span>{t.gettext('Rain precipitation')}</span>
                </label>
                <label>
                    <input type="checkbox" checked={includeAnalysis}
                           onChange={event => setIncludeAnalysis(event.target.checked)}/>
                    <span>{t.gettext('Include analysis')}</span>
                </label>
            </div>

            {(errors.length > 0 || downloadError) && (
                <div className="pdfValidationMessages" role="alert">
                    {errors.map(error => <div key={error}>{error}</div>)}
                    {downloadError && <div>{downloadError}</div>}
                </div>
            )}

            <div className="pdfModalActions">
                <button type="button" className="pdfCancelButton" onClick={closeMethod} disabled={submitting}>
                    {t.gettext('Cancel')}
                </button>
                <button type="button" className="pdfDownloadButton" onClick={download}
                        disabled={errors.length > 0 || submitting}>
                    {submitting ? t.gettext('Generating PDF...') : t.gettext('Get PDF report')}
                </button>
            </div>
        </div>
    );
};

export default WeatherStationPdfReportModal;
